import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Plus, Trash2, Dices, Trophy, Star, Radio, Calendar, Clock, Hash,
} from "lucide-react";
import {
  adminVerify, clearToken, fetchResults, fetchUpdates,
  adminCreateResult, adminDeleteResult, adminCreateUpdate, adminDeleteUpdate,
} from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import { toast } from "../hooks/use-toast";
import { todayISO } from "../mock";

function ResultManager({ kind, label, Icon }) {
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);

  // Sort time strings like "10:05 AM", "1:35 PM" in reverse chronological order (latest first)
  const sortByTime = (arr) => {
    return [...arr].sort((a, b) => {
      const toMinutes = (t) => {
        const match = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!match) return 0;
        let [, hour, min, period] = match;
        hour = parseInt(hour, 10);
        min = parseInt(min, 10);
        if (period.toUpperCase() === "PM" && hour !== 12) hour += 12;
        if (period.toUpperCase() === "AM" && hour === 12) hour = 0;
        return hour * 60 + min;
      };
      return toMinutes(b.time) - toMinutes(a.time);
    });
  };

  const load = useCallback(() =>
    fetchResults(kind, { date }).then((d) => setRows(sortByTime(d)))
  , [kind, date]);

  useEffect(() => { load(); }, [load]);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!date || !time || !number) return;
    setBusy(true);
    try {
      await adminCreateResult(kind, { date, time, number });
      setTime(""); setNumber("");
      await load();
      toast({ title: "Added", description: `${label} result saved.` });
    } catch (err) {
      toast({ title: "Failed", description: err?.response?.data?.detail || "Could not save", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    try {
      await adminDeleteResult(kind, id);
      await load();
      toast({ title: "Deleted", description: "Entry removed." });
    } catch {
      toast({ title: "Failed", description: "Could not delete", variant: "destructive" });
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2">
        <div className="rounded-2xl border border-white/5 bg-[#11172a] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon className="w-4 h-4 text-amber-300" />
            <h3 className="text-base font-semibold text-slate-100">Add {label} Result</h3>
          </div>
          <form onSubmit={onAdd} className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> Date
              </label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-[#0e1424] border-white/10 text-slate-100"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> Time (e.g. 10:05 AM)
              </label>
              <Input
                placeholder="10:05 AM"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="bg-[#0e1424] border-white/10 text-slate-100"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 flex items-center gap-1.5">
                <Hash className="w-3 h-3" /> Number
              </label>
              <Input
                placeholder={kind === "starline" ? "e.g. 568-9" : "e.g. 96"}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                className="bg-[#0e1424] border-white/10 text-slate-100 font-mono"
              />
            </div>
            <Button
              type="submit"
              disabled={busy || !time || !number}
              className="w-full bg-amber-400 hover:bg-amber-300 text-[#0b0f1a] font-semibold"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add Entry
            </Button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-3">
        <div className="rounded-2xl border border-white/5 bg-[#11172a] overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-100">{label} — {date}</div>
            <span className="text-[11px] uppercase tracking-wider text-slate-500">{rows.length} entries</span>
          </div>
          <div className="max-h-[460px] overflow-auto">
            {rows.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-sm">No entries for this date.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#0e1424] text-left text-[11px] uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="px-5 py-2.5 font-medium">Time</th>
                    <th className="px-5 py-2.5 font-medium">Number</th>
                    <th className="px-5 py-2.5 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                      <td className="px-5 py-2.5 text-slate-300 font-mono">{r.time}</td>
                      <td className="px-5 py-2.5 text-amber-300 font-mono font-bold">{r.number}</td>
                      <td className="px-5 py-2.5 text-right">
                        <button
                          onClick={() => onDelete(r.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function UpdatesManager() {
  const [items, setItems] = useState([]);
  const [time, setTime] = useState("");
  const [oldN, setOldN] = useState("");
  const [fresh, setFresh] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => fetchUpdates().then(setItems);
  useEffect(() => { load(); }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!time || !oldN || !fresh) return;
    setBusy(true);
    try {
      await adminCreateUpdate({ time, old: oldN, fresh });
      setTime(""); setOldN(""); setFresh("");
      await load();
      toast({ title: "Added", description: "Live update saved." });
    } catch (err) {
      toast({ title: "Failed", description: err?.response?.data?.detail || "Could not save", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    try {
      await adminDeleteUpdate(id);
      await load();
    } catch {
      toast({ title: "Failed", description: "Could not delete", variant: "destructive" });
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2">
        <div className="rounded-2xl border border-white/5 bg-[#11172a] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="w-4 h-4 text-amber-300" />
            <h3 className="text-base font-semibold text-slate-100">Add Live Update</h3>
          </div>
          <form onSubmit={onAdd} className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Time (e.g. 07:15 AM)</label>
              <Input value={time} onChange={(e) => setTime(e.target.value)} placeholder="07:15 AM"
                className="bg-[#0e1424] border-white/10 text-slate-100" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Old</label>
                <Input value={oldN} onChange={(e) => setOldN(e.target.value)} placeholder="30"
                  className="bg-[#0e1424] border-white/10 text-slate-100 font-mono" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">New</label>
                <Input value={fresh} onChange={(e) => setFresh(e.target.value)} placeholder="06"
                  className="bg-[#0e1424] border-white/10 text-slate-100 font-mono" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-amber-400 hover:bg-amber-300 text-[#0b0f1a] font-semibold">
              <Plus className="w-4 h-4 mr-1.5" /> Add Update
            </Button>
          </form>
        </div>
      </div>
      <div className="lg:col-span-3">
        <div className="rounded-2xl border border-white/5 bg-[#11172a] overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 text-sm font-semibold text-slate-100">
            Live Updates ({items.length})
          </div>
          <div className="max-h-[460px] overflow-auto">
            {items.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-sm">No live updates yet.</div>
            ) : (
              <ul className="divide-y divide-white/5">
                {items.map((u) => (
                  <li key={u.id} className="px-5 py-3 flex items-center gap-4 hover:bg-white/[0.02]">
                    <span className="text-amber-300 font-mono text-sm w-24">{u.time}</span>
                    <span className="text-rose-300 font-mono">{u.old}</span>
                    <span className="text-slate-500">→</span>
                    <span className="text-emerald-300 font-mono font-bold">{u.fresh}</span>
                    <button
                      onClick={() => onDelete(u.id)}
                      className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md text-rose-300 hover:text-rose-200 hover:bg-rose-500/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    adminVerify()
      .then(() => setReady(true))
      .catch(() => {
        clearToken();
        navigate("/admin/login", { replace: true });
      });
  }, [navigate]);

  const onLogout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-[#0b0f1a] flex items-center justify-center text-slate-400">
        Verifying session…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0f1a] text-slate-100">
      <header className="border-b border-amber-400/15 bg-[#0a0e17]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center">
              <Dices className="w-4 h-4 text-[#0b0f1a]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-sm font-bold text-amber-300 leading-tight">Madhur Lottery</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Admin Panel</div>
            </div>
          </Link>
          <Button
            onClick={onLogout}
            variant="outline"
            className="bg-transparent border-white/10 text-slate-200 hover:bg-white/5"
          >
            <LogOut className="w-4 h-4 mr-1.5" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Manage Results & Updates</h1>
        <p className="text-slate-400 text-sm mt-1">Add daily numbers, manage live ticker updates.</p>

        <Tabs defaultValue="madhur" className="mt-6">
          <TabsList className="bg-[#11172a] border border-white/5">
            <TabsTrigger value="madhur" className="data-[state=active]:bg-amber-400/15 data-[state=active]:text-amber-200">
              <Trophy className="w-4 h-4 mr-1.5" /> Madhur
            </TabsTrigger>
            <TabsTrigger value="starline" className="data-[state=active]:bg-amber-400/15 data-[state=active]:text-amber-200">
              <Star className="w-4 h-4 mr-1.5" /> Starline
            </TabsTrigger>
            <TabsTrigger value="updates" className="data-[state=active]:bg-amber-400/15 data-[state=active]:text-amber-200">
              <Radio className="w-4 h-4 mr-1.5" /> Live Updates
            </TabsTrigger>
          </TabsList>
          <TabsContent value="madhur" className="mt-6">
            <ResultManager kind="madhur" label="Madhur" Icon={Trophy} />
          </TabsContent>
          <TabsContent value="starline" className="mt-6">
            <ResultManager kind="starline" label="Starline" Icon={Star} />
          </TabsContent>
          <TabsContent value="updates" className="mt-6">
            <UpdatesManager />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
